import type { ApiClient } from '@classgo/app-kit';
import type {
  CreateSchoolUserInput,
  PermissionView,
  RoleTemplateInput,
  RoleTemplateView,
  SchoolUserView,
  SetUserOverridesInput,
  SetUserTemplatesInput,
  UpdateSchoolUserInput,
  UserAccessView,
} from '@classgo/shared';
import { queryOptions } from '@tanstack/react-query';

/**
 * The users and roles endpoints (#129): who works in the school, the permission templates it
 * composes from the catalog, and the exceptions set on one person.
 *
 * @param {ApiClient} api -- The client.
 * @returns The calls.
 */
export const usersApi = (api: ApiClient) => ({
  createUser: (body: CreateSchoolUserInput) =>
    api.request<{ id: string }>('/users', { method: 'POST', body }),
  updateUser: (id: string, body: UpdateSchoolUserInput) =>
    api.request<{ ok: boolean }>(`/users/${id}`, { method: 'PATCH', body }),
  setTemplates: (id: string, body: SetUserTemplatesInput) =>
    api.request<UserAccessView>(`/users/${id}/roles`, { method: 'PUT', body }),
  setOverrides: (id: string, body: SetUserOverridesInput) =>
    api.request<UserAccessView>(`/users/${id}/permissions`, { method: 'PUT', body }),
  createRole: (body: RoleTemplateInput) =>
    api.request<RoleTemplateView>('/roles', { method: 'POST', body }),
  updateRole: (id: string, body: RoleTemplateInput) =>
    api.request<RoleTemplateView>(`/roles/${id}`, { method: 'PATCH', body }),
  deleteRole: (id: string) => api.request<{ ok: boolean }>(`/roles/${id}`, { method: 'DELETE' }),
});

/**
 * The reads of the users and roles screens.
 */
export const usersQueries = {
  users: (api: ApiClient) =>
    queryOptions({
      queryKey: ['users', 'list'],
      queryFn: () => api.request<SchoolUserView[]>('/users'),
    }),
  access: (api: ApiClient, userId: string) =>
    queryOptions({
      queryKey: ['users', 'access', userId],
      queryFn: () => api.request<UserAccessView>(`/users/${userId}/access`),
      enabled: userId !== '',
    }),
  roles: (api: ApiClient) =>
    queryOptions({
      queryKey: ['users', 'roles'],
      queryFn: () => api.request<RoleTemplateView[]>('/roles'),
    }),
  permissions: (api: ApiClient) =>
    queryOptions({
      queryKey: ['users', 'permissions'],
      queryFn: () => api.request<PermissionView[]>('/permissions'),
      staleTime: 5 * 60 * 1000,
    }),
};
